import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router';
import {
  Box,
  Typography,
  Button,
  Grid,
  Snackbar,
  Alert,
  Switch,
  FormControlLabel,
  Divider,
  Autocomplete,
  Chip,
  TextField
} from '@mui/material';

// ----- Komponen custom -----
import CustomFormLabel from '../../../forms/theme-elements/CustomFormLabel';
import CustomTextField from '../../../forms/theme-elements/CustomTextField';
import RegisterTooltip from '../../../forms/RegisterTooltip';
import withRegisterFormTooltips from 'src/hoc/withRegisterFormTooltips';

// ----- Hook & Utils -----
import { useFormHandler } from '../../../../utils/useFormHandler';
import { emailRecipientsValidationSchema } from '../../../../utils/validationSchemas';
import {
  registerEmailRecipient,
  updateEmailRecipient,
  fetchEmailRecipientById,
} from '../../../../services/EmailRecipientsService';
import { fetchDistinctVendorsFromAPI } from '../../../../services/VendorService';

const EmailRecipientRegisterForm = () => {
  const navigate = useNavigate();
  const { state } = useLocation();

  // Support multiple ways of passing data:
  // 1. From detail page: state.emailRecipients (full object)
  // 2. From listing: state.emailRecipientId (just ID)
  // 3. Direct object: state.emailRecipient (full object)
  const emailRecipientFromState = state?.emailRecipient || state?.emailRecipients;
  const emailRecipientIdFromState = state?.emailRecipientId;
  
  const [emailRecipient, setEmailRecipient] = useState(emailRecipientFromState);
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [vendorsLoading, setVendorsLoading] = useState(false);
  
  const emailRecipientId = emailRecipient?.id || emailRecipientIdFromState;
  const isEditing = !!emailRecipientId;

  // Fetch vendors for autocomplete
  useEffect(() => {
    const loadVendors = async () => {
      setVendorsLoading(true);
      try {
        const response = await fetchDistinctVendorsFromAPI();
        console.log('Vendors response:', response);
        // Response structure from fetchDistinctVendorsFromAPI: 
        // response.data.data = { vendors: { data: [...] } }
        // So response = { vendors: { data: [...] } }
        let vendorsData = [];
        if (response?.vendors?.data && Array.isArray(response.vendors.data)) {
          vendorsData = response.vendors.data;
        } else if (response?.data?.vendors?.data && Array.isArray(response.data.vendors.data)) {
          vendorsData = response.data.vendors.data;
        } else if (Array.isArray(response?.data)) {
          vendorsData = response.data;
        } else if (Array.isArray(response)) {
          vendorsData = response;
        }
        console.log('Vendors data:', vendorsData, 'Length:', vendorsData.length);
        setVendors(vendorsData);
      } catch (error) {
        console.error('Error fetching vendors:', error);
        setVendors([]);
      } finally {
        setVendorsLoading(false);
      }
    };
    loadVendors();
  }, []);

  // Snackbar states
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  // Fetch email recipient data if only ID is provided
  useEffect(() => {
    if (isEditing && emailRecipientIdFromState && !emailRecipient) {
      setIsLoadingTemplate(true);
      fetchEmailRecipientById(emailRecipientIdFromState)
        .then((data) => {
          const res = data?.data?.emailRecipient;
          if (res) {
            setEmailRecipient(res);
          }
        })
        .catch((err) => {
          console.error('Error fetching email recipient:', err);
        })
        .finally(() => {
          setIsLoadingTemplate(false);
        });
    }
  }, [isEditing, emailRecipientIdFromState, emailRecipient]);

  // Ensure apiFunction is always defined
  const apiFunction = isEditing ? updateEmailRecipient : registerEmailRecipient;

  // Success Callback
  const onSuccess = () => {
    setSnackbarMessage(isEditing ? 'Email recipient updated successfully!' : 'Email recipient created successfully!');
    setSnackbarSeverity('success');
    setOpenSnackbar(true);

    setTimeout(() => {
      navigate('/managements/email-recipients');
    }, 1000);
  };

  // Error Callback
  const onError = (error) => {
    setSnackbarMessage(error.message || 'An error occurred');
    setSnackbarSeverity('error');
    setOpenSnackbar(true);
  };

  // Helper function to parse email string to array
  const parseEmailsToArray = (emailString) => {
    if (!emailString) return [];
    return emailString
      .split(/[,\n]/)
      .map(email => email.trim())
      .filter(email => email.length > 0);
  };

  // Helper function to convert array to string
  const convertArrayToString = (emailArray) => {
    if (!emailArray || emailArray.length === 0) return '';
    return emailArray.join(', ');
  };

  // Parse initial email addresses to array
  const initialEmailArray = useMemo(() => {
    return parseEmailsToArray(emailRecipient?.email_address || '');
  }, [emailRecipient?.email_address]);

  const formik = useFormHandler(
    {
      recipient_name: emailRecipient?.recipient_name || '',
      email_address: emailRecipient?.email_address || '',
      description: emailRecipient?.description || '',
      is_active: emailRecipient?.is_active !== undefined ? emailRecipient.is_active : true,
      is_vendor: emailRecipient?.is_vendor || false,
      vendor_code: emailRecipient?.vendor_code || ''
    },
    emailRecipientsValidationSchema,
    apiFunction,
    onSuccess,
    onError,
    emailRecipientId || null
  );

  // Update formik values when emailRecipient data is loaded
  useEffect(() => {
    if (emailRecipient && isEditing) {
      formik.setValues({
        recipient_name: emailRecipient.recipient_name || '',
        email_address: emailRecipient.email_address || '',
        description: emailRecipient.description || '',
        is_active: emailRecipient.is_active !== undefined ? emailRecipient.is_active : true,
        is_vendor: emailRecipient.is_vendor || false,
        vendor_code: emailRecipient.vendor_code || ''
      });
      // Update email chips as well
      const parsed = parseEmailsToArray(emailRecipient.email_address || '');
      setEmailChips(parsed);
    }
  }, [emailRecipient, isEditing]); // eslint-disable-line react-hooks/exhaustive-deps

  // State for email chips (array)
  const [emailChips, setEmailChips] = useState(initialEmailArray);

  // Update email chips when formik value changes (for edit mode)
  useEffect(() => {
    if (emailRecipient?.email_address) {
      const parsed = parseEmailsToArray(emailRecipient.email_address);
      setEmailChips(parsed);
    } else if (!emailRecipient) {
      // Reset for new form
      setEmailChips([]);
    }
  }, [emailRecipient?.email_address, emailRecipient]);

  // Update formik value when chips change
  const handleEmailChipsChange = (newChips) => {
    setEmailChips(newChips);
    const emailString = convertArrayToString(newChips);
    formik.setFieldValue('email_address', emailString);
  };

  // Show loading state while fetching data
  if (isLoadingTemplate) {
    return (
      <Box p={3} display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Loading email recipient data...</Typography>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h5" mb={3}>
        {isEditing ? 'Edit Email Recipient' : 'Create Email Recipient'}
      </Typography>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <form onSubmit={formik.handleSubmit} noValidate>
        <Grid container spacing={3}>
          {/* Basic Information */}
          <Grid item xs={12}>
            <Typography variant="h5" gutterBottom>
              Email Recipient Information
            </Typography>
            <Divider sx={{ mb: 3 }} />
          </Grid>

          {/* Recipient Name */}
          <Grid item xs={12}>
            <CustomFormLabel htmlFor="recipient_name">Recipient Name *</CustomFormLabel>
            <CustomTextField
              id="recipient_name"
              fullWidth
              placeholder="e.g., manager"
              tooltipKey="emailRecipient.recipient_name"
              {...formik.getFieldProps('recipient_name')}
              error={formik.touched.recipient_name && Boolean(formik.errors.recipient_name)}
              helperText={formik.touched.recipient_name && formik.errors.recipient_name}
            />
          </Grid>

          {/* Email Address - Multiple with Chips */}
          <Grid item xs={12}>
            <CustomFormLabel htmlFor="email_address">Email Address *</CustomFormLabel>
            <RegisterTooltip tooltipKey="emailRecipient.email_address">
              <Autocomplete
              multiple
              freeSolo
              id="email_address"
              options={[]}
              value={emailChips}
              onChange={(event, newValue) => {
                // Handle when chips are added/removed
                const lastValue = newValue[newValue.length - 1];
                if (typeof lastValue === 'string' && lastValue.includes(',')) {
                  // Handle paste with multiple emails
                  const emails = parseEmailsToArray(lastValue);
                  const filtered = emails.filter(email => !emailChips.includes(email));
                  handleEmailChipsChange([...emailChips, ...filtered]);
                } else {
                  handleEmailChipsChange(newValue);
                }
              }}
              onInputChange={(event, newInputValue, reason) => {
                // Handle when user types comma or presses Enter
                if (reason === 'input' && newInputValue) {
                  if (newInputValue.includes(',')) {
                    const emails = parseEmailsToArray(newInputValue);
                    const filtered = emails.filter(email => !emailChips.includes(email));
                    if (filtered.length > 0) {
                      handleEmailChipsChange([...emailChips, ...filtered]);
                      // Clear input
                      if (event && event.target) {
                        event.target.value = '';
                      }
                    }
                  }
                }
              }}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={index}
                    label={option}
                    color="primary"
                    sx={{
                      backgroundColor: '#1976d2',
                      color: '#fff',
                      '& .MuiChip-deleteIcon': {
                        color: '#fff',
                        '&:hover': {
                          color: '#fff',
                        },
                      },
                    }}
                  />
                ))
              }
                renderInput={(params) => (
                  <CustomTextField
                    {...params}
                    placeholder="Type email and press Enter or comma to add"
                    error={formik.touched.email_address && Boolean(formik.errors.email_address)}
                    helperText={formik.touched.email_address && formik.errors.email_address || "Type email addresses and press Enter or comma to add as chips. You can also paste multiple emails separated by comma or newline."}
                    tooltipKey="emailRecipient.email_address"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && e.target.value.trim()) {
                        e.preventDefault();
                        const trimmed = e.target.value.trim();
                        if (trimmed && !emailChips.includes(trimmed)) {
                          handleEmailChipsChange([...emailChips, trimmed]);
                          // Clear input
                          setTimeout(() => {
                            if (e.target) {
                              e.target.value = '';
                            }
                          }, 0);
                        }
                      }
                    }}
                    onPaste={(e) => {
                      // Handle paste event
                      const pastedText = e.clipboardData.getData('text');
                      if (pastedText) {
                        const emails = parseEmailsToArray(pastedText);
                        const filtered = emails.filter(email => !emailChips.includes(email));
                        if (filtered.length > 0) {
                          e.preventDefault();
                          handleEmailChipsChange([...emailChips, ...filtered]);
                        }
                      }
                    }}
                  />
                )}
              />
            </RegisterTooltip>
          </Grid>

          {/* Description */}
          <Grid item xs={12}>
            <CustomFormLabel htmlFor="description">Description</CustomFormLabel>
            <CustomTextField
              id="description"
              fullWidth
              multiline
              rows={3}
              placeholder="Enter description (optional)"
              tooltipKey="emailRecipient.description"
              {...formik.getFieldProps('description')}
              error={formik.touched.description && Boolean(formik.errors.description)}
              helperText={formik.touched.description && formik.errors.description}
            />
          </Grid>

          {/* Is Vendor */}
          <Grid item xs={12}>
            <RegisterTooltip tooltipKey="emailRecipient.is_vendor">
              <FormControlLabel
                control={
                  <Switch
                    checked={formik.values.is_vendor || false}
                    onChange={(e) => {
                      formik.setFieldValue('is_vendor', e.target.checked);
                      if (!e.target.checked) {
                        formik.setFieldValue('vendor_code', '');
                      }
                    }}
                  />
                }
                label="Is Vendor"
              />
            </RegisterTooltip>
          </Grid>

          {/* Vendor Code - Only show if is_vendor is true */}
          {formik.values.is_vendor && (
            <Grid item xs={12}>
              <CustomFormLabel htmlFor="vendor_code">Vendor Code *</CustomFormLabel>
              <RegisterTooltip tooltipKey="emailRecipient.vendor_code">
                <Autocomplete
                  id="vendor_code"
                  options={vendors || []}
                  getOptionLabel={(option) => {
                    if (!option) return '';
                    if (typeof option === 'string') return option;
                    return `${option.vendor_code || ''} - ${option.vendor_name || ''}`;
                  }}
                  value={vendors.find(v => v && v.vendor_code === formik.values.vendor_code) || null}
                  isOptionEqualToValue={(option, value) => {
                    if (!option || !value) return false;
                    return option.vendor_code === value.vendor_code;
                  }}
                  onChange={(event, newValue) => {
                    formik.setFieldValue('vendor_code', newValue?.vendor_code || '');
                  }}
                  loading={vendorsLoading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select vendor code"
                      error={formik.touched.vendor_code && Boolean(formik.errors.vendor_code)}
                      helperText={formik.touched.vendor_code && formik.errors.vendor_code}
                    />
                  )}
                  renderOption={(props, option) => {
                    if (!option) return null;
                    return (
                      <Box component="li" {...props} key={option.vendor_code || option.id}>
                        <Box>
                          <Typography variant="body1" fontWeight="bold">
                            {option.vendor_code || ''}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {option.vendor_name || ''}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  }}
                />
              </RegisterTooltip>
            </Grid>
          )}

          {/* Active Status */}
          <Grid item xs={12}>
            <RegisterTooltip tooltipKey="emailRecipient.is_active">
              <FormControlLabel
                control={
                  <Switch
                    checked={formik.values.is_active}
                    onChange={(e) => formik.setFieldValue('is_active', e.target.checked)}
                  />
                }
                label="Active"
              />
            </RegisterTooltip>
          </Grid>
        </Grid>

        {/* Action Buttons */}
        <Box mt={3} sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            onClick={() => navigate('/managements/email-recipients')}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={formik.isSubmitting}
          >
            {formik.isSubmitting ? 'Saving...' : (isEditing ? 'Update Recipient' : 'Create Recipient')}
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default withRegisterFormTooltips(EmailRecipientRegisterForm, 'Isi data sesuai kebutuhan field ini.');

